import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * Scales a fixed-width form "desk" (the column of 8.5in sheets) down to fit a
 * portrait phone screen using a CSS transform.
 *
 * Why a transform and not `zoom`: iOS Safari clamps font-size to a minimum, so
 * when `zoom` shrinks the layout far enough the boxes keep shrinking but the
 * text hits that floor and stops — leaving oversized text in tiny boxes.
 * `transform: scale()` scales the already-rendered pixels, so it ignores the
 * font floor and shrinks text and boxes together.
 *
 * The transform doesn't reflow, so this also:
 *   - scales around the top-center (the sheets are centred in the desk), so the
 *     scaled sheet lands inside the viewport with no horizontal overflow, and
 *   - sets the desk's height to its scaled height, collapsing the empty space
 *     the un-reflowed layout box would otherwise leave below the content.
 *
 * Only active on portrait phones; on tablet/desktop/landscape it clears itself
 * so the normal CSS `zoom` styling applies.
 */
@Directive({
  selector: '[appScaleToFit]',
})
export class ScaleToFitDirective implements AfterViewInit, OnDestroy {
  // Selector for the fixed-width sheet inside the desk. Defaults to the CIR/TOF
  // sheet; the CMC passes its own (appScaleToFit=".cmf").
  @Input('appScaleToFit') sheet_selector: string = ''

  // Portrait phones only — matches the CSS breakpoint that neutralises `zoom`.
  private readonly mql = window.matchMedia('(max-width: 600px) and (orientation: portrait)');
  // A little side breathing room so the sheet edges/shadow aren't flush.
  private readonly sideMargin = 12;
  // The sheet keeps growing after the first measure (logo image, web fonts,
  // async form data), and the frozen desk height would clip its bottom edge.
  private sheetSize: ResizeObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    // Run outside Angular so resize churn doesn't trigger change detection.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onChange, { passive: true });
      window.addEventListener('orientationchange', this.onChange, { passive: true });
      // Defer so the sheets have laid out (and any async form data rendered).
      setTimeout(() => {
        this.onChange();
        const sheet = this.el.nativeElement.querySelector<HTMLElement>(this.sheet_selector || '.rv-sheet');
        if (sheet && 'ResizeObserver' in window) {
          // The transform scales the desk, not the sheet's own box, so this never re-fires on its own change.
          this.sheetSize = new ResizeObserver(this.onChange);
          this.sheetSize.observe(sheet);
        }
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.sheetSize?.disconnect();
    window.removeEventListener('resize', this.onChange);
    window.removeEventListener('orientationchange', this.onChange);
  }

  private onChange = (): void => {
    const desk = this.el.nativeElement;

    // Always reset first so a rotation back to landscape/desktop is clean.
    desk.style.transform = '';
    desk.style.transformOrigin = '';
    desk.style.height = '';
    desk.style.width = '';
    desk.style.overflowX = '';

    if (!this.mql.matches) return;

    const sheet = desk.querySelector<HTMLElement>(this.sheet_selector || '.rv-sheet');
    if (!sheet) return;

    const sheetWidth = sheet.offsetWidth;
    if (!sheetWidth) return;

    // Viewport-ish width the scaled desk must end up occupying.
    const containerWidth = desk.clientWidth;
    const available = containerWidth - this.sideMargin * 2;
    const scale = available / sheetWidth;
    if (scale >= 1) return; // already fits — leave it alone

    // Widen the desk so that (deskWidth × scale) === containerWidth. The
    // desk paints the grey page background; without this its ~viewport-wide
    // box shrinks with the transform and its background shows as a narrow
    // centred strip (visible above the first sheet) instead of filling the
    // screen edge-to-edge.
    desk.style.width = `${containerWidth / scale}px`;

    // Measure the natural (un-transformed) height before applying the scale,
    // after the width is set so any reflow is accounted for.
    const naturalHeight = desk.scrollHeight;

    desk.style.transformOrigin = 'top left';
    desk.style.transform = `scale(${scale})`;
    // CRITICAL: the desk's overflow clip is applied in its own coordinate
    // space BEFORE its transform — with the CSS `overflow-x: auto` in effect
    // the 8.5in sheet would be clipped first and THEN scaled, leaving a
    // narrow strip with both edges cut off. Let it overflow; the transform
    // shrinks the full sheet into the viewport, so the post-transform bounds
    // fit and no horizontal scrolling appears.
    desk.style.overflowX = 'visible';
    desk.style.height = `${Math.ceil(naturalHeight * scale)}px`;
  };
}
